<script setup lang="ts">
import type { PayoutOptionTone } from '~/constants/receiptModel'
import IconCheck from '~icons/material-symbols/check'
import IconExpandLess from '~icons/material-symbols/expand-less'
import IconExpandMore from '~icons/material-symbols/expand-more'
import { getPayoutOptionDisplay } from '~/constants/receiptModel'

interface ModelSelectColumn {
  key: 'model' | 'supplier' | 'service'
  label: string
  value: string
}

interface ModelSelectItem {
  code: string
  letter: string
  tone: PayoutOptionTone
  modelLabel: string
  serviceLabel: string
  columns: ModelSelectColumn[]
}

const props = withDefaults(defineProps<{
  options: string[]
  modelValue: string
  disabled?: boolean
  readonly?: boolean
  id?: string
}>(), {
  disabled: false,
  readonly: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const OPEN_KEYS = ['ArrowDown', 'ArrowUp', 'Enter', ' ']

const { t } = useI18n()

const fallbackId = useId()
const triggerId = computed(() => props.id || `receipt-model-select-${fallbackId}`)
const listboxId = computed(() => `${triggerId.value}-listbox`)

const rootRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLElement | null>(null)
const isOpen = ref(false)
const activeIndex = ref(-1)

const items = computed(() => props.options.map(code => toModelSelectItem(code)))

const selectedIndex = computed(() => {
  const index = props.options.indexOf(props.modelValue)
  return index >= 0 ? index : 0
})

const selectedItem = computed<ModelSelectItem | undefined>(() => items.value[selectedIndex.value])

const isInteractive = computed(() => items.value.length > 0 && !props.disabled && !props.readonly)

const triggerAriaLabel = computed(() => {
  if (!selectedItem.value)
    return undefined

  return t('receipt-model.model_select.aria_label', {
    model: selectedItem.value.modelLabel,
    service: selectedItem.value.serviceLabel,
  })
})

const activeDescendant = computed(() => {
  if (!isOpen.value || activeIndex.value < 0 || activeIndex.value >= items.value.length)
    return undefined

  return getOptionId(activeIndex.value)
})

function toModelSelectItem(code: string): ModelSelectItem {
  const display = getPayoutOptionDisplay(code)
  const serviceLabel = t(display.serviceLabelKey, display.serviceLabelParams)

  return {
    code,
    letter: display.letter,
    tone: display.tone,
    modelLabel: display.modelLabel,
    serviceLabel,
    columns: [
      { key: 'model', label: t('receipt-model.model_select.column_model'), value: display.modelLabel },
      { key: 'supplier', label: t('receipt-model.model_select.column_supplier'), value: t('receipt-model.model_select.supplier_in_cash') },
      { key: 'service', label: t('receipt-model.model_select.column_service'), value: serviceLabel },
    ],
  }
}

function getOptionId(index: number) {
  return `${triggerId.value}-option-${index}`
}

function focusTrigger() {
  triggerRef.value?.focus()
}

function open() {
  if (!isInteractive.value)
    return

  activeIndex.value = selectedIndex.value
  isOpen.value = true
}

function close() {
  isOpen.value = false
  activeIndex.value = -1
}

function toggle() {
  if (isOpen.value)
    close()
  else
    open()
}

function selectOption(index: number) {
  close()
  focusTrigger()

  if (index < 0 || index >= props.options.length)
    return

  const code = props.options[index]
  if (code !== props.modelValue)
    emit('update:modelValue', code)
}

function moveActive(index: number) {
  const lastIndex = items.value.length - 1
  activeIndex.value = Math.min(Math.max(index, 0), lastIndex)
}

function onTriggerKeydown(event: KeyboardEvent) {
  if (!isInteractive.value)
    return

  if (!isOpen.value) {
    if (OPEN_KEYS.includes(event.key)) {
      event.preventDefault()
      open()
    }
    return
  }

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      moveActive(activeIndex.value + 1)
      break
    case 'ArrowUp':
      event.preventDefault()
      moveActive(activeIndex.value - 1)
      break
    case 'Home':
      event.preventDefault()
      moveActive(0)
      break
    case 'End':
      event.preventDefault()
      moveActive(items.value.length - 1)
      break
    case 'Enter':
    case ' ':
      event.preventDefault()
      selectOption(activeIndex.value)
      break
    case 'Escape':
      // Keeps Esc from also closing a modal/overlay around the selector.
      event.preventDefault()
      event.stopPropagation()
      close()
      focusTrigger()
      break
    case 'Tab':
      close()
      break
  }
}

// Capture phase: closing on an outside click doesn't depend on other components letting the event propagate.
function onDocumentPointerDown(event: PointerEvent) {
  if (event.target instanceof Node && rootRef.value?.contains(event.target))
    return

  close()
}

function removeDocumentListener() {
  document.removeEventListener('pointerdown', onDocumentPointerDown, true)
}

watch(isOpen, (value) => {
  if (value)
    document.addEventListener('pointerdown', onDocumentPointerDown, true)
  else
    removeDocumentListener()
})

watch(isInteractive, (value) => {
  if (!value)
    close()
})

watch(activeIndex, (index) => {
  if (!isOpen.value || index < 0)
    return

  nextTick(() => {
    document.getElementById(getOptionId(index))?.scrollIntoView({ block: 'nearest' })
  })
})

onBeforeUnmount(removeDocumentListener)
</script>

<template>
  <div
    v-if="selectedItem"
    ref="rootRef"
    class="receipt-model-select"
  >
    <!-- Read-only uses role="textbox": a combobox requires aria-expanded (ARIA 1.2). -->
    <div
      :id="triggerId"
      ref="triggerRef"
      :role="props.readonly ? 'textbox' : 'combobox'"
      class="receipt-model-select__trigger"
      :class="{
        'receipt-model-select__trigger--disabled': props.disabled,
        'receipt-model-select__trigger--readonly': props.readonly,
      }"
      :tabindex="props.disabled ? undefined : 0"
      :aria-haspopup="props.readonly ? undefined : 'listbox'"
      :aria-expanded="props.readonly ? undefined : isOpen"
      :aria-controls="props.readonly ? undefined : listboxId"
      :aria-activedescendant="activeDescendant"
      :aria-disabled="props.disabled || undefined"
      :aria-readonly="props.readonly || undefined"
      :aria-label="triggerAriaLabel"
      data-testid="receipt-model-select-trigger"
      @click="toggle"
      @keydown="onTriggerKeydown"
    >
      <span class="receipt-model-select__avatar-box" aria-hidden="true">
        <span
          class="receipt-model-select__avatar"
          :class="`receipt-model-select__avatar--${selectedItem.tone}`"
        >
          {{ selectedItem.letter }}
        </span>
      </span>

      <span class="receipt-model-select__columns">
        <span
          v-for="column in selectedItem.columns"
          :key="column.key"
          class="receipt-model-select__column"
          :class="`receipt-model-select__column--${column.key}`"
        >
          <span class="receipt-model-select__label">{{ column.label }}</span>
          <span class="receipt-model-select__value">{{ column.value }}</span>
        </span>
      </span>

      <span
        v-if="!props.readonly"
        class="receipt-model-select__indicator"
        aria-hidden="true"
      >
        <IconExpandLess v-if="isOpen" class="receipt-model-select__chevron" />
        <IconExpandMore v-else class="receipt-model-select__chevron" />
      </span>
    </div>

    <div
      v-show="isOpen"
      :id="listboxId"
      role="listbox"
      class="receipt-model-select__listbox"
      :aria-label="t('receipt-model.model_select.listbox_label')"
      @mousedown.prevent
    >
      <div
        v-for="(item, index) in items"
        :id="getOptionId(index)"
        :key="item.code"
        role="option"
        class="receipt-model-select__option"
        :class="{ 'receipt-model-select__option--active': index === activeIndex }"
        :aria-selected="index === selectedIndex"
        :data-testid="`receipt-model-select-option-${item.code}`"
        @click="selectOption(index)"
        @mouseenter="activeIndex = index"
      >
        <span class="receipt-model-select__avatar-box" aria-hidden="true">
          <span
            class="receipt-model-select__avatar"
            :class="`receipt-model-select__avatar--${item.tone}`"
          >
            {{ item.letter }}
          </span>
        </span>

        <span class="receipt-model-select__columns">
          <span
            v-for="column in item.columns"
            :key="column.key"
            class="receipt-model-select__column"
            :class="`receipt-model-select__column--${column.key}`"
          >
            <span class="receipt-model-select__label">{{ column.label }}</span>
            <span class="receipt-model-select__value">{{ column.value }}</span>
          </span>
        </span>

        <span class="receipt-model-select__indicator" aria-hidden="true">
          <IconCheck v-if="index === selectedIndex" class="receipt-model-select__check" />
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Values without a token: 12px radius, dropdown shadow, 28px avatar, 70px/110px columns, A/B/C colors.
.receipt-model-select {
  @apply relative w-full;

  &__trigger,
  &__option {
    @apply flex w-full items-center gap-2xs px-3xs py-4xs;
  }

  &__trigger {
    @apply bg-neutral-high-pure border-a-xs border-solid border-neutral-high-medium rounded-[12px];
    @apply cursor-pointer select-none outline-none;

    &:focus-visible {
      @apply outline-focused;
    }

    &--readonly {
      @apply cursor-default;
    }

    &--disabled {
      @apply bg-neutral-high-light cursor-not-allowed;

      .receipt-model-select__label {
        @apply text-brand-primary-light;
      }

      .receipt-model-select__value {
        @apply text-neutral-low-light opacity-intense;
      }

      .receipt-model-select__chevron {
        @apply text-neutral-high-dark;
      }
    }
  }

  &__avatar-box {
    @apply flex flex-shrink-0 items-center justify-center w-xs h-xs;
  }

  &__avatar {
    @apply flex items-center justify-center w-[28px] h-[28px] rounded-full;
    @apply fonts-body-small-bold text-neutral-high-pure;

    &--a {
      background-color: #1c8c61;
    }

    &--b {
      background-color: #eda633;
    }

    &--c {
      background-color: #d14a45;
    }

    &--neutral {
      @apply bg-brand-primary-light;
    }
  }

  &__columns {
    @apply flex flex-1 flex-wrap items-start min-w-0 gap-x-2xs gap-y-nano;
  }

  &__column {
    @apply flex flex-col gap-quark;

    &--model {
      @apply flex-shrink-0 w-[70px];
    }

    &--supplier {
      @apply flex-shrink-0 w-[110px];
    }

    &--service {
      @apply min-w-0;

      flex: 1 1 10rem;
    }
  }

  &__label {
    @apply fonts-body-small-regular text-neutral-low-light;
  }

  &__value {
    @apply fonts-body-medium-bold text-neutral-low-dark break-words;
  }

  &__indicator {
    @apply flex flex-shrink-0 items-center justify-center w-2xs h-2xs;
  }

  &__chevron {
    @apply w-2xs h-2xs text-neutral-low-light;
  }

  &__check {
    @apply w-3xs h-3xs text-feedback-positive-pure;
  }

  // z-10: above the step numbers (z-0), below the layout's sticky header (z-19).
  &__listbox {
    @apply absolute left-0 right-0 top-full z-10 mt-nano overflow-hidden;
    @apply bg-neutral-high-pure border-a-xs border-solid border-neutral-high-medium rounded-[12px];

    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  &__option {
    @apply cursor-pointer;

    & + & {
      @apply border-t-xs border-solid border-neutral-high-medium;
    }

    &--active {
      @apply bg-neutral-high-medium bg-opacity-medium;
    }
  }
}
</style>
